import { MeasurementPoint, MeasurementRole, ProfileRole } from '@core/models/installation.model';

export interface AddableRoles {
  inProfile: MeasurementRole[];
  outsideProfile: MeasurementRole[];
}

// Inside the profile a role gets another point only when it takes more than one; a single role
// already has its point. A measured role no profile can hold is offered outside the profile, a
// single one only while no active point holds it. A point added there stays visible, because only
// removing a role from the profile hides points. Calculated roles take no channel, so never.
export function addableRoles(
  catalogue: readonly MeasurementRole[],
  profileRoles: readonly ProfileRole[],
  activePoints: readonly MeasurementPoint[],
): AddableRoles {
  const inProfile = new Set(
    profileRoles.filter(role => role.state === 'active').map(role => role.role),
  );
  const withPoint = new Set(
    activePoints.filter(point => point.state === 'active').map(point => point.role),
  );
  const offered = catalogue.filter(role => !role.deprecated);
  return {
    inProfile: offered.filter(
      role => role.selectable && role.multiplicity === 'multiple' && inProfile.has(role.role),
    ),
    outsideProfile: offered.filter(
      role =>
        !role.selectable &&
        role.nature === 'measured' &&
        (role.multiplicity === 'multiple' || !withPoint.has(role.role)),
    ),
  };
}

export interface AddPointRole {
  role: string;
  displayName: string;
  unit: string;
  // The role has its point and takes no other, so the dialog lists it without letting it be chosen.
  added: boolean;
}

// What the Add dialog lists, in catalogue order: every role that may get a point, and every other
// role that already has one.
export function addPointRoles(
  catalogue: readonly MeasurementRole[],
  addable: AddableRoles,
  activePoints: readonly MeasurementPoint[],
): AddPointRole[] {
  const choosable = new Set([...addable.inProfile, ...addable.outsideProfile].map(r => r.role));
  const withPoint = new Set(
    activePoints.filter(point => point.state === 'active').map(point => point.role),
  );
  return catalogue
    .filter(role => choosable.has(role.role) || withPoint.has(role.role))
    .map(({ role, displayName, unit }) => ({
      role,
      displayName,
      unit,
      added: !choosable.has(role),
    }));
}
